import { useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, IconButton, Tab, Tabs } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Container from "@mui/material/Container";
import {
  ApiErrorHandler,
  OrganizationContext,
  useSyncQueueList,
} from "hhn-commons";
import {
  ISyncQueueItem,
  ISyncQueueQueryParams,
  SocDiscipline,
  SyncQueueStatus,
} from "@home-health-notify/models";
import { useSnackbar } from "notistack";
import { NeedsReviewTable } from "./NeedsReviewTable";
import { HistoryTable } from "./HistoryTable";
import HistoryControls from "./HistoryControls";
import SyncQueueDrawer from "./SyncQueueDrawer";
import RejectPatientModal from "./RejectPatientModal";
import ApproveConfirmDialog from "./ApproveConfirmDialog";
import { ListingHeading } from "../common/ListingHeader.style";
import {
  HeaderActions,
  HeaderBar,
  HeaderOrg,
  HeaderTitles,
  ScreenRoot,
} from "./EmrPatients.style";
import { matchesSearch, patientName } from "./syncQueueUtils";

const NEEDS_REVIEW_TAB = 0;

type SyncQueueContentProps = {
  // Standalone modal use passes onClose (renders a title + close button).
  // When embedded (e.g. inside the "More" area) both are omitted.
  onClose?: () => void;
  embedded?: boolean;
};

export function SyncQueueContent({ onClose, embedded }: SyncQueueContentProps) {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const organization = selectedOrgUser?.getOrganization();
  const orgId = organization?.organizationId ?? "";
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(NEEDS_REVIEW_TAB);
  const [historyStatus, setHistoryStatus] = useState<
    SyncQueueStatus | undefined
  >(undefined);
  const [historySearch, setHistorySearch] = useState("");
  const [selected, setSelected] = useState<ISyncQueueItem | null>(null);

  const [approveTarget, setApproveTarget] = useState<ISyncQueueItem | null>(
    null,
  );
  const [isApproving, setIsApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const [rejectTarget, setRejectTarget] = useState<ISyncQueueItem | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);

  const isActing = isApproving || isRejecting;

  const params = useMemo<ISyncQueueQueryParams>(() => ({}), []);
  const { syncQueue, isLoading, refetch } = useSyncQueueList(params);

  const pending = useMemo(
    () => syncQueue.items.filter(item => item.status === "pending"),
    [syncQueue.items],
  );
  const history = useMemo(
    () =>
      syncQueue.items
        .filter(item => item.status !== "pending")
        .filter(item => !historyStatus || item.status === historyStatus)
        .filter(item => matchesSearch(item, historySearch)),
    [syncQueue.items, historyStatus, historySearch],
  );

  // Keep the open drawer in sync with refreshed data; close it if the row is gone.
  useEffect(() => {
    if (!selected) return;
    const fresh = syncQueue.items.find(item => item._id === selected._id);
    setSelected(fresh ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncQueue.items]);

  const openApprove = (item: ISyncQueueItem) => {
    setApproveError(null);
    setApproveTarget(item);
  };

  const closeApprove = () => {
    if (isApproving) return;
    setApproveTarget(null);
    setApproveError(null);
  };

  const confirmApprove = async (socDiscipline: SocDiscipline) => {
    if (!selectedOrgUser || !approveTarget) return;
    try {
      setIsApproving(true);
      const outcome = await selectedOrgUser.syncQueueManager.approve(
        approveTarget._id,
        socDiscipline,
      );
      if (outcome.outcome === "created") {
        enqueueSnackbar("Patient created in HHN", { variant: "success" });
        setApproveTarget(null);
        setSelected(null);
        refetch();
      } else if (outcome.outcome === "skipped") {
        enqueueSnackbar("Patient already exists in HHN — skipped", {
          variant: "info",
        });
        setApproveTarget(null);
        setSelected(null);
        refetch();
      } else {
        // "error" — the row keeps its updated status (e.g. blocked_on_mapping).
        setApproveError(
          "Couldn't create this patient. Review its status for details.",
        );
        refetch();
      }
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
      setApproveTarget(null);
    } finally {
      setIsApproving(false);
    }
  };

  const openReject = (item: ISyncQueueItem) => setRejectTarget(item);

  const closeReject = () => {
    if (isRejecting) return;
    setRejectTarget(null);
  };

  const submitReject = async (comment: string) => {
    if (!selectedOrgUser || !rejectTarget) return;
    try {
      setIsRejecting(true);
      await selectedOrgUser.syncQueueManager.reject(rejectTarget._id, comment);
      enqueueSnackbar("Patient rejected", { variant: "success" });
      setRejectTarget(null);
      setSelected(null);
      refetch();
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    } finally {
      setIsRejecting(false);
    }
  };

  const onFixAndCreate = () => {
    enqueueSnackbar("Complete & create is coming soon", { variant: "info" });
  };

  return (
    <ScreenRoot>
      <Container maxWidth="lg" data-testid="emr-patients">
        {embedded ? (
          <HeaderBar>
            <HeaderTitles>
              <ListingHeading variant="h6">EMR Patients</ListingHeading>
              <HeaderOrg>
                Review patients arriving from your EMR and decide what enters
                HHN.
              </HeaderOrg>
            </HeaderTitles>
            <HeaderActions>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                data-testid="refresh-emr-patients"
              >
                Refresh
              </Button>
            </HeaderActions>
          </HeaderBar>
        ) : (
          <HeaderBar>
            <HeaderTitles>
              <ListingHeading variant="h6">EMR Patients</ListingHeading>
              <HeaderOrg>{organization?.organizationName}</HeaderOrg>
            </HeaderTitles>
            <HeaderActions>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                data-testid="refresh-emr-patients"
              >
                Refresh
              </Button>
              {onClose && (
                <IconButton
                  size="large"
                  color="inherit"
                  data-testid="close-emr-patients"
                  onClick={onClose}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </HeaderActions>
          </HeaderBar>
        )}

        <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value);
            setSelected(null);
          }}
        >
          <Tab
            data-testid="tab-needs-review"
            label={
              <Badge
                color="primary"
                badgeContent={pending.length}
                sx={{ paddingRight: "12px" }}
              >
                Needs Review
              </Badge>
            }
          />
          <Tab data-testid="tab-history" label="History" />
        </Tabs>

        {tab === NEEDS_REVIEW_TAB ? (
          <NeedsReviewTable
            items={pending}
            isLoading={isLoading}
            actingId={isActing ? "acting" : null}
            onRowClick={setSelected}
            onApprove={openApprove}
            onReject={openReject}
          />
        ) : (
          <>
            <HistoryControls
              status={historyStatus}
              onStatusChange={setHistoryStatus}
              search={historySearch}
              onSearchChange={setHistorySearch}
            />
            <HistoryTable
              items={history}
              isLoading={isLoading}
              orgId={orgId}
              onRowClick={setSelected}
            />
          </>
        )}
      </Container>

      {selected && (
        <SyncQueueDrawer
          item={selected}
          orgId={orgId}
          isActing={isActing}
          onClose={() => setSelected(null)}
          onApprove={openApprove}
          onReject={openReject}
          onFixAndCreate={onFixAndCreate}
        />
      )}

      <ApproveConfirmDialog
        item={approveTarget}
        isSubmitting={isApproving}
        errorReason={approveError}
        onConfirm={confirmApprove}
        onClose={closeApprove}
      />

      <RejectPatientModal
        isOpen={rejectTarget !== null}
        patientName={rejectTarget ? patientName(rejectTarget) : ""}
        onClose={closeReject}
        onSubmit={submitReject}
        isSubmitting={isRejecting}
      />
    </ScreenRoot>
  );
}
